import React, { useState, useEffect, useRef } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import { sha256 } from 'js-sha256';


const NFCModal = ({ client, onClose, onSubmit }) => {
NFCModal.displayName = 'NFCModal';
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [nfcData, setNfcData] = useState(null);
  const [tagSerialNumber, setTagSerialNumber] = useState(null);
  const [isNdefReaderAvailable, setIsNdefReaderAvailable] = useState(false);
  const abortControllerRef = useRef(null);
  const writeCompleteRef = useRef(false);
  const verifyCompleteRef = useRef(false);
  

  const steps = [
    { id: 'init', title: 'Initialisation', description: 'Préparation des données pour l\'enregistrement NFC' },
    { id: 'place', title: 'Placement du tag', description: 'Veuillez placer le tag NFC près de l\'appareil' },
    { id: 'write', title: 'Écriture du tag', description: 'Enregistrement en cours... Maintenez le tag en place' },
    { id: 'verify', title: 'Vérification', description: 'Veuillez placer à nouveau le tag pour vérifier' },
    { id: 'complete', title: 'Terminé', description: 'Enregistrement NFC complété avec succès' }
  ];

  const generateNfcData = (serialNumber) => {
    const cid = uuid();
    const secretKey = "Son_of_the_Bitch!"; // À remplacer par une vraie clé secrète
    const hash = sha256(cid + secretKey + client.id);

    // Gérer l'adresse qu'elle soit une chaîne ou un objet structuré
    let addressToEncode;
    if (typeof client.address === 'object' && client.address !== null) {
      // Nouvelle structure avec plusieurs champs
      const { street, postalCode, city, country } = client.address;
      const parts = [];
      if (street) parts.push(street);
      if (postalCode) parts.push(postalCode);
      if (city) parts.push(city);
      if (country) parts.push(country);
      addressToEncode = parts.join(', ');
    } else {
      // Ancienne structure (chaîne simple)
      addressToEncode = client.address || '';
    }

    const addr = btoa(addressToEncode);
    const exp = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    // URL simple qui s'ouvre automatiquement sur Android et iOS
    const url = "https://agenda.omadservices.com";

    return {
      cid,
      clientId: client.id, // Ajout de l'ID du client
      hash: hash.slice(0, 20),
      addr,
      exp,
      serialNumber,
      url
    };
  };

  useEffect(() => {
    setIsNdefReaderAvailable('NDEFReader' in window);
    if (step === 0) {
      setTimeout(() => setStep(1), 1000);
    }
  }, [step]);

  const handleNfcOperation = async () => {
    if (!isNdefReaderAvailable) {
      setError("NFC n'est pas supporté sur cet appareil.");
      return;
    }

    try {
      abortControllerRef.current = new AbortController();
      const ndef = new window.NDEFReader();

      // Étape 1: Scan initial pour détecter le tag
      if (step === 1 && !writeCompleteRef.current) {
        await ndef.scan({ signal: abortControllerRef.current.signal });

        ndef.addEventListener("reading", async (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          console.log("Tag détecté pour écriture, serial number:", event.serialNumber);

          const { serialNumber } = event;
          setTagSerialNumber(serialNumber);
          setStep(2);

          // IMPORTANT: Arrêter le scan avant d'écrire
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }

          try {
            const dataToWrite = generateNfcData(serialNumber);
            console.log("Données à écrire:", dataToWrite);

            // Attendre que le scan soit bien arrêté
            await new Promise(resolve => setTimeout(resolve, 200));

            // Créer une nouvelle instance NDEFReader pour l'écriture
            const ndefWriter = new window.NDEFReader();

            // Créer un record pour les données JSON
            const jsonRecord = { recordType: "text", data: JSON.stringify(dataToWrite) };

            // Créer un record pour l'URL
            const urlRecord = { recordType: "url", data: dataToWrite.url };

            // Écriture NFC avec les deux records
            await ndefWriter.write({ records: [jsonRecord, urlRecord] });

            console.log("Écriture réussie");
            setNfcData(dataToWrite);
            writeCompleteRef.current = true;
            setStep(3);
          } catch (writeError) {
            console.error("Erreur d'écriture:", writeError);
            setError("Erreur lors de l'écriture du tag. " + writeError.message);
          }
        }, { once: true }); // Une seule lecture pour l'écriture
      }

      // Étape 3: Scan pour vérification
      else if (step === 3 && !verifyCompleteRef.current) {
        await ndef.scan({ signal: abortControllerRef.current.signal });

        ndef.addEventListener("reading", async (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          console.log("Tag détecté pour vérification");

          const { message } = event;

          try {
            if (message && message.records) {
              let verifiedData = null;
              let urlData = null;

              for (const record of message.records) {
                // Utiliser 'utf-8' comme encodage par défaut
                const textDecoder = new TextDecoder(record.encoding || 'utf-8');
                const decodedData = textDecoder.decode(record.data);

                if (record.recordType === "text") {
                  try {
                    verifiedData = JSON.parse(decodedData);
                  } catch (error) {
                    console.error("Erreur lors du parsing JSON:", error);
                  }
                } else if (record.recordType === "url") {
                  urlData = decodedData;
                }
              }

              console.log("Données lues:", verifiedData);
              console.log("URL lue:", urlData);

              if (verifiedData && urlData) {
                // Comparer uniquement les champs pertinents
                const relevantFields = ['cid', 'clientId', 'hash', 'addr', 'exp', 'serialNumber'];
                const areDataEqual = relevantFields.every(field => verifiedData[field] === nfcData[field]);

                if (areDataEqual && urlData === nfcData.url) {
                  console.log("Vérification réussie");
                  setStep(4);
                  verifyCompleteRef.current = true;
                  if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                  }
                } else {
                  console.error("Données non correspondantes");
                  setError("La vérification a échoué. Les données ne correspondent pas.");
                  if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                  }
                }
              } else {
                setError("Données manquantes dans le tag NFC.");
                if (abortControllerRef.current) {
                  abortControllerRef.current.abort();
                }
              }
            }
          } catch (verifyError) {
            console.error("Erreur de vérification:", verifyError);
            setError("Erreur lors de la vérification du tag. " + verifyError.message);
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
          }
        }, { once: true }); // Une seule lecture pour la vérification
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Erreur NFC:", error);
        setError("Erreur lors de l'accès au NFC. " + error.message);
      }
    }
  };
  
  
  

  useEffect(() => {
    if (step === 1 || step === 3) {
      handleNfcOperation();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [step]);


  const handleSubmit = () => {
    onSubmit({ ...nfcData, tagSerialNumber });
  };

  const renderStepContent = () => {
    const currentStep = steps[step];
    return (
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">{currentStep.title}</h3>
        <p>{currentStep.description}</p>
        {error && (
          <div className="mt-4 text-red-500 flex items-center justify-center">
            <AlertTriangle size={20} className="mr-2" />
            {error}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-xl mx-auto w-fit max-w-md">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Configuration NFC pour {client.firstName} {client.lastName}</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <X size={24} />
        </button>
      </div>

        <div className="mb-6">
          {renderStepContent()}
        </div>

        <div className="flex justify-center space-x-2">
          {steps.map((s, index) => (
            <div
              key={s.id}
              className={`w-4 h-4 rounded-full ${
                index === step ? 'bg-blue-500' : 
                index < step ? 'bg-green-500' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {step === 4 && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              <Check size={20} className="mr-2 inline" />
              Terminer
            </button>
          </div>
        )}
    </div>
  );
};

export default NFCModal;
